/**
 * ScreenshotManager
 * Manages screenshot capture, storage, and upload
 * Uses JPEG format (80% quality) for smaller file sizes
 * Implements retry logic with exponential backoff
 */
import { getPublicApiOrigin } from '../config/env';

class ScreenshotManager {
  constructor(canvasRef, sessionId) {
    this.canvas = canvasRef;
    this.sessionId = sessionId;
    this.screenshots = [];
    this.screenshotTypes = {
      FACE_DETECTION: 'face_detection',
      SELFIE: 'selfie',
      PROFILE_PHOTO: 'profile_photo', // report header photo (D-044)
      RANDOM: 'random',
      VIOLATION: 'violation'
    };
  }

  /**
   * Capture screenshot from canvas
   * @param {string} type - Type of screenshot (face_detection, selfie, random, violation)
   * @param {object} metadata - Additional metadata (confidence, etc.)
   * @returns {Promise<object|null>} Screenshot object or null if failed
   */
  async capture(type, metadata = {}) {
    if (!this.canvas.current) {
      console.warn('⚠️ Canvas not available for screenshot');
      return null;
    }

    try {
      // Capture from canvas as JPEG (80% quality for smaller size)
      const dataURL = this.canvas.current.toDataURL('image/jpeg', 0.8);
      
      const screenshot = {
        type,
        timestamp: Date.now(),
        sessionId: this.sessionId,
        metadata,
        image: dataURL,
        size: dataURL.length,
        uploaded: false
      };

      // Store locally first (for reliability)
      this.screenshots.push(screenshot);
      console.log(`📸 Screenshot captured: ${type} (${Math.round(dataURL.length / 1024)}KB)`);

      // Upload asynchronously (don't block UI)
      this.uploadScreenshot(screenshot).catch(err => {
        console.error('❌ Screenshot upload failed, will retry:', err);
      });

      return screenshot;
    } catch (error) {
      console.error('❌ Error capturing screenshot:', error);
      return null;
    }
  }

  /**
   * Upload screenshot to backend with retry logic
   * @param {object} screenshot - Screenshot object
   * @returns {Promise<void>}
   */
  async uploadScreenshot(screenshot) {
    const maxRetries = 3;
    
    for (let i = 0; i < maxRetries; i++) {
      try {
        const response = await fetch(
          `${getPublicApiOrigin()}/api/v1/sessions/${this.sessionId}/screenshots`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('access_token') || localStorage.getItem('token') || ''}`
            },
            body: JSON.stringify({
              type: screenshot.type,
              timestamp: screenshot.timestamp,
              image: screenshot.image,
              metadata: screenshot.metadata
            })
          }
        );

        if (response.ok) {
          const result = await response.json();
          screenshot.uploaded = true;
          screenshot.filepath = result.filepath;
          console.log(`✅ Screenshot uploaded: ${screenshot.type}`);
          return;
        } else {
          throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        if (i === maxRetries - 1) {
          console.error(`❌ Screenshot upload failed after ${maxRetries} attempts:`, error);
          throw error;
        }
        // Exponential backoff: 1s, 2s, 4s
        const delay = 1000 * Math.pow(2, i);
        console.log(`⏳ Retrying screenshot upload in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  /**
   * Upload all pending screenshots
   * Called at end of interview
   * @returns {Promise<void>}
   */
  async uploadPending() {
    const pending = this.screenshots.filter(s => !s.uploaded);
    if (pending.length === 0) {
      console.log('✅ All screenshots already uploaded');
      return;
    }

    console.log(`📤 Uploading ${pending.length} pending screenshots...`);
    try {
      await Promise.all(pending.map(s => this.uploadScreenshot(s)));
      console.log('✅ All pending screenshots uploaded');
    } catch (error) {
      console.error('❌ Error uploading pending screenshots:', error);
    }
  }

  /**
   * Get screenshot statistics
   * @returns {object} Stats object
   */
  getStats() {
    return {
      total: this.screenshots.length,
      uploaded: this.screenshots.filter(s => s.uploaded).length,
      pending: this.screenshots.filter(s => !s.uploaded).length,
      byType: this.screenshots.reduce((acc, s) => {
        acc[s.type] = (acc[s.type] || 0) + 1;
        return acc;
      }, {})
    };
  }
}

export default ScreenshotManager;

